import dayjs from 'dayjs';
import IMask from 'imask';
import type MaskedDynamic from 'imask/esm/masked/dynamic';

import type { RangeConfig } from 'src/types/range-picker';
import type { Values } from 'src/types/utils';

export const TZ = 'UTC';
export const NOW = 'Now';
export const DEFAULT_DATE_TEMPLATE = 'DD.MM.YY';

export const PRESET_ID = {
  '2_DAYS': 'twoDays',
  '7_DAYS': 'sevenDays',
  '14_DAYS': 'fourteenDays',
  '30_DAYS': 'thirtyDays',
  '60_DAYS': 'sixtyDays',
} as const;

export const OTHER_PRESET_ID = {
  YESTERDAY: 'yesterday',
  PREVIOUS_WEEK: 'previousWeek',
  PREVIOUS_MONTH: 'previousMonth',
  THIS_WEEK: 'thisWeek',
  THIS_MONTH: 'thisMonth',
} as const;

const buildLabel =
  (label: string) =>
  (prefix: string = '') =>
    `${prefix}${label}`;

export type RangePresetId = Values<typeof PRESET_ID> | Values<typeof OTHER_PRESET_ID>;

export const RANGE_CONFIG: Record<string, RangeConfig<RangePresetId>> = {
  LAST_2: {
    id: PRESET_ID['2_DAYS'],
    getLabel: buildLabel(PRESET_ID['2_DAYS']),
    getValue: (now) => [dayjs(now.startOf('day').add(-2, 'days')), now],
    getStrValue: (now) => [dayjs(now.startOf('day').add(-2, 'days')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  LAST_7: {
    id: PRESET_ID['7_DAYS'],
    getLabel: buildLabel(PRESET_ID['7_DAYS']),
    getValue: (now) => [dayjs(now.add(-7, 'days').startOf('day')), now],
    getStrValue: (now) => [dayjs(now.add(-7, 'days').startOf('day')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  LAST_14: {
    id: PRESET_ID['14_DAYS'],
    getLabel: buildLabel(PRESET_ID['14_DAYS']),
    getValue: (now) => [dayjs(now.add(-14, 'days').startOf('day')), now],
    getStrValue: (now) => [dayjs(now.add(-14, 'days').startOf('day')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  LAST_30: {
    id: PRESET_ID['30_DAYS'],
    getLabel: buildLabel(PRESET_ID['30_DAYS']),
    getValue: (now) => [dayjs(now.add(-30, 'days').startOf('day')), now],
    getStrValue: (now) => [dayjs(now.add(-30, 'days').startOf('day')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  LAST_60: {
    id: PRESET_ID['60_DAYS'],
    getLabel: buildLabel(PRESET_ID['60_DAYS']),
    getValue: (now) => [dayjs(now.add(-60, 'days').startOf('day')), now],
    getStrValue: (now) => [dayjs(now.add(-60, 'days').startOf('day')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  YESTERDAY: {
    id: OTHER_PRESET_ID.YESTERDAY,
    getLabel: buildLabel(OTHER_PRESET_ID.YESTERDAY),
    getValue: (now) => [dayjs(now.add(-1, 'days').startOf('day')), now.add(-1, 'days').endOf('day')],
    getStrValue: (now) => [
      dayjs(now.add(-1, 'days').startOf('day')).format(DEFAULT_DATE_TEMPLATE),
      now.add(-1, 'days').endOf('day').format(DEFAULT_DATE_TEMPLATE),
    ],
  },
  PREVIOUS_WEEK: {
    id: OTHER_PRESET_ID.PREVIOUS_WEEK,
    getLabel: buildLabel(OTHER_PRESET_ID.PREVIOUS_WEEK),
    getValue: (now) => [dayjs(now.add(-1, 'week').startOf('week')), now.add(-1, 'week').endOf('week')],
    getStrValue: (now) => [
      dayjs(now.add(-1, 'week').startOf('week')).format(DEFAULT_DATE_TEMPLATE),
      now.add(-1, 'week').endOf('week').format(DEFAULT_DATE_TEMPLATE),
    ],
  },
  THIS_WEEK: {
    id: OTHER_PRESET_ID.THIS_WEEK,
    getLabel: buildLabel(OTHER_PRESET_ID.THIS_WEEK),
    getValue: (now) => [dayjs(now.startOf('week')), now],
    getStrValue: (now) => [dayjs(now.startOf('week')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
  PREVIOUS_MONTH: {
    id: OTHER_PRESET_ID.PREVIOUS_MONTH,
    getLabel: buildLabel(OTHER_PRESET_ID.PREVIOUS_MONTH),
    getValue: (now) => [dayjs(now.startOf('month').add(-1, 'month')), now.add(-1, 'month').endOf('month')],
    getStrValue: (now) => [
      dayjs(now.add(-1, 'month').startOf('month')).format(DEFAULT_DATE_TEMPLATE),
      now.add(-1, 'month').endOf('month').format(DEFAULT_DATE_TEMPLATE),
    ],
  },
  THIS_MONTH: {
    id: OTHER_PRESET_ID.THIS_MONTH,
    getLabel: buildLabel(OTHER_PRESET_ID.THIS_MONTH),
    getValue: (now) => [dayjs(now.startOf('month')), now],
    getStrValue: (now) => [dayjs(now.startOf('month')).format(DEFAULT_DATE_TEMPLATE), 'Now'],
  },
};

export const RANGE_CONFIG_BY_PRESET_ID_MAP: Record<
  Values<typeof PRESET_ID> | Values<typeof OTHER_PRESET_ID>,
  RangeConfig<RangePresetId>
> = {
  [PRESET_ID['2_DAYS']]: RANGE_CONFIG.LAST_2,
  [PRESET_ID['7_DAYS']]: RANGE_CONFIG.LAST_7,
  [PRESET_ID['14_DAYS']]: RANGE_CONFIG.LAST_14,
  [PRESET_ID['30_DAYS']]: RANGE_CONFIG.LAST_30,
  [PRESET_ID['60_DAYS']]: RANGE_CONFIG.LAST_60,
  [OTHER_PRESET_ID.YESTERDAY]: RANGE_CONFIG.YESTERDAY,
  [OTHER_PRESET_ID.PREVIOUS_WEEK]: RANGE_CONFIG.PREVIOUS_WEEK,
  [OTHER_PRESET_ID.PREVIOUS_MONTH]: RANGE_CONFIG.PREVIOUS_MONTH,
  [OTHER_PRESET_ID.THIS_WEEK]: RANGE_CONFIG.THIS_WEEK,
  [OTHER_PRESET_ID.THIS_MONTH]: RANGE_CONFIG.THIS_MONTH,
};

export const PLACEHOLDER_CHAR = '\u2000';
export const DATE_MASK_PATTERN = 'DD.`MM.`YY';

export const NOW_FORMAT_SPECIFIER_EXP = /(d|w|M|y)/;

export const DATE_TEMPLATE_MASK = {
  blocks: {
    DD: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 31,
      placeholderChar: PLACEHOLDER_CHAR,
    },
    MM: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      placeholderChar: PLACEHOLDER_CHAR,
    },
    YY: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 99,
      placeholderChar: PLACEHOLDER_CHAR,
    },
  },
  mask: DATE_MASK_PATTERN,
  overwrite: false,
  autofix: false,
  lazy: true,
};

export const DYNAMIC_DATE_TEMPLATE_MASK = {
  mask: [
    DATE_TEMPLATE_MASK,
    {
      mask: `{${NOW}[m]0[00]#`,
      definitions: {
        '#': NOW_FORMAT_SPECIFIER_EXP,
        m: /-/,
      },
      skipInvalid: false,
      lazy: true,
      autofix: false,
      overwrite: false,
    },
  ],
  dispatch: (appended: string, dynamicMasked: MaskedDynamic) => {
    const value = String(dynamicMasked.value + appended);
    const isTypedChar = /[A-Za-z]/.test(value);
    const isDateMask = /\d/.test(value.charAt(0)) || /[:.]/.test(value);

    return isTypedChar && !isDateMask ? dynamicMasked.compiledMasks[1] : dynamicMasked.compiledMasks[0];
  },
};

export const RANGE_PICKER_ERRORS_MAP = {
  isFromAfterTo: 'fromAfterTo',
  isFromInvalid: 'invalidDate',
  isFromInvalidFormat: 'invalidFormat',
  isToBeforeFrom: 'toBeforeFrom',
  isToInvalid: 'invalidDate',
  isToInvalidFormat: 'invalidFormat',
  isFromBeforeDisabled: 'beforeDisabled',
  isToAfterDisabled: 'afterDisabled',
  isOutOfRange: 'outOfRange',
};
